import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatStepperModule } from '@angular/material/stepper';
import { ReactiveFormsModule } from '@angular/forms';
import { ContainerTypeService } from '../../core/services/container-type.service';
import { MaintenanceTypeService } from '../../core/services/maintenance-type.service';
import { MaintenanceService } from '../../core/services/maintenance.service';
import { RoleService } from '../../core/services/role.service';
import { TaskService } from '../../core/services/task.service';
import { AcceptanceCriteriaService } from '../../core/services/acceptance-criteria.service';
import { StageCreateComponent } from '../maintenance-create/stage-create/stage-create.component';
import { HttpErrorResponse } from '@angular/common/http';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'app-maintenance-update',
  templateUrl: './maintenance-update.component.html',
  styleUrls: ['./maintenance-update.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatExpansionModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatStepperModule,
    MatProgressBarModule,
    StageCreateComponent,
    MatCheckboxModule,
  ],
})
export class MaintenanceUpdateComponent implements OnInit {
  firstFormGroup!: FormGroup;
  secondFormGroup!: FormGroup;
  containerTypes: any[] = [];
  maintenanceTypes: any[] = [];
  roles: any[] = [];
  statuses: any[] = [
    { value: true, viewValue: 'Activo' },
    { value: false, viewValue: 'Inactivo' },
  ];
  errorMessage: string | null = null;

  tasks: any[] = [];
  accordionStages: FormArray = this.fb.array([]);
  acceptanceCriterias: any[] = [];
  maintenanceId!: number;
  showProgressBar: boolean = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private containerTypeService: ContainerTypeService,
    private maintenanceTypeService: MaintenanceTypeService,
    private maintenanceService: MaintenanceService,
    private roleService: RoleService,
    private taskService: TaskService,
    private acceptanceCriteriaService: AcceptanceCriteriaService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.maintenanceId = +this.route.snapshot.paramMap.get('id')!;

    this.firstFormGroup = this.fb.group({
      containerType: ['', Validators.required],
      maintenanceType: ['', Validators.required],
      role: ['', Validators.required],
      status: ['', Validators.required],
    });

    this.secondFormGroup = this.fb.group({
      stages: this.accordionStages,
    });

    this.loadContainerTypes();
    this.loadMaintenanceTypes();
    this.loadRoles();
    this.loadTasks();
    this.loadAcceptanceCriterias();
    this.loadMaintenanceDetails(this.maintenanceId);
  }

  loadContainerTypes() {
    this.containerTypeService.getContainerTypes().subscribe(data => {
      this.containerTypes = data;
    });
  }

  loadMaintenanceTypes() {
    this.maintenanceTypeService.getMaintenanceTypes().subscribe(data => {
      this.maintenanceTypes = data;
    });
  }

  loadRoles() {
    this.roleService.getRoles().subscribe(data => {
      this.roles = data;
    });
  }

  loadTasks() {
    this.taskService.getTasks().subscribe(data => {
      this.tasks = data;
    });
  }

  loadAcceptanceCriterias() {
    this.acceptanceCriteriaService.getAcceptanceCriteria().subscribe(data => {
      this.acceptanceCriterias = data;
    });
  }

  loadMaintenanceDetails(id: number) {
    this.maintenanceService.getMaintenance(id).subscribe(data => {
      this.firstFormGroup.patchValue({
        containerType: data.containerType.id,
        maintenanceType: data.maintenanceType.id,
        role: data.rol.id,
        status: data.status,
      });
      this.populateStages(data.stages);
      this.cdr.detectChanges(); // Marcar manualmente la detección de cambios
    });
  }

  populateStages(stages: any[]) {
    stages.forEach((stage: any) => {
      const stageGroup = this.fb.group({
        stageName: [stage.stageName, Validators.required],
        order: [stage.order, Validators.required],
        required: [stage.required],
        stageMaintenanceId: [stage.stageMaintenanceId, Validators.required],
        tasks: this.fb.array([]),
      });

      stage.tasks.forEach((task: any) => {
        const taskGroup = this.fb.group({
          taskId: [task.taskId, Validators.required],
          taskName: [task.taskName],
          order: [task.order, Validators.required],
          required: [task.required],
          elements: this.fb.array([]),
          acceptanceCriterias: this.fb.array([]),
          visation: this.fb.group({
            id: [task.visation?.id || null],
            effectiveDate: [task.visation?.effectiveDate || ''],
            visationActions: this.fb.group({
              id: [task.visation?.visationActions?.id || null],
              observation: [task.visation?.visationActions?.observation || ''],
              filePath: [task.visation?.visationActions?.filePath || ''],
              originalFileName: [task.visation?.visationActions?.originalFileName || ''],
              action: this.fb.group({
                id: [task.visation?.visationActions?.action?.id || null],
                name: [task.visation?.visationActions?.action?.name || ''],
              }),
            }),
          }),
        });

        task.elements.forEach((element: any) => {
          const elementGroup = this.fb.group({
            taskElementId: [element.taskElementId],
            elementType: [element.elementType],
            name: [element.name],
            order: [element.order, Validators.required],
            required: [element.required],
            labelName: [element.label],
            options: this.fb.array([]),
            visation: this.fb.group({
              id: [element.visation?.id || null],
              effectiveDate: [element.visation?.effectiveDate || ''],
              visationActions: this.fb.group({
                id: [element.visation?.visationActions?.id || null],
                observation: [element.visation?.visationActions?.observation || ''],
                filePath: [element.visation?.visationActions?.filePath || ''],
                originalFileName: [element.visation?.visationActions?.originalFileName || ''],
                action: this.fb.group({
                  id: [element.visation?.visationActions?.action?.id || null],
                  name: [element.visation?.visationActions?.action?.name || ''],
                }),
              }),
            }),
          });

          element.options.forEach((option: any) => {
            const optionGroup = this.fb.group({
              elementOptionId: [option.elementOptionId],
              id: [option.id],
              elements: this.fb.array([]),
            });

            option.elements.forEach((subOption: any) => {
              (optionGroup.get('elements') as FormArray).push(
                this.fb.group({
                  elementOptionRelationshipId: [subOption.elementOptionRelationshipId],
                  elementType: [subOption.elementType],
                  label: [subOption.label],
                  order: [subOption.order, Validators.required],
                  required: [subOption.required],
                  labelName: [subOption.labelName],
                }),
              );
            });

            (elementGroup.get('options') as FormArray).push(optionGroup);
          });

          (taskGroup.get('elements') as FormArray).push(elementGroup);
        });

        task.acceptanceCriterias.forEach((criteria: any) => {
          const criteriaGroup = this.fb.group({
            taskAcceptanceCriteriaId: [criteria.taskAcceptanceCriteriaId],
            acceptanceCriteriaId: [criteria.acceptanceCriteriaId],
            name: [criteria.name],
            order: [criteria.order, Validators.required],
            required: [criteria.required],
            elements: this.fb.array([]),
            visation: this.fb.group({
              id: [criteria.visation?.id || null],
              effectiveDate: [criteria.visation?.effectiveDate || ''],
              visationActions: this.fb.group({
                id: [criteria.visation?.visationActions?.id || null],
                observation: [criteria.visation?.visationActions?.observation || ''],
                filePath: [criteria.visation?.visationActions?.filePath || ''],
                originalFileName: [criteria.visation?.visationActions?.originalFileName || ''],
                action: this.fb.group({
                  id: [criteria.visation?.visationActions?.action?.id || null],
                  name: [criteria.visation?.visationActions?.action?.name || ''],
                }),
              }),
            }),
          });

          criteria.elements.forEach((element: any) => {
            const elementGroup = this.fb.group({
              elementAcceptanceCriteria: [element.elementAcceptanceCriteria],
              elementType: [element.elementType],
              name: [element.name],
              order: [element.order, Validators.required],
              required: [element.required],
              labelName: [element.label],
              options: this.fb.array([]),
              visation: this.fb.group({
                id: [element.visation?.id || null],
                effectiveDate: [element.visation?.effectiveDate || ''],
                visationActions: this.fb.group({
                  id: [element.visation?.visationActions?.id || null],
                  observation: [element.visation?.visationActions?.observation || ''],
                  filePath: [element.visation?.visationActions?.filePath || ''],
                  originalFileName: [element.visation?.visationActions?.originalFileName || ''],
                  action: this.fb.group({
                    id: [element.visation?.visationActions?.action?.id || null],
                    name: [element.visation?.visationActions?.action?.name || ''],
                  }),
                }),
              }),
            });

            element.options.forEach((option: any) => {
              const optionGroup = this.fb.group({
                elementOptionId: [option.elementOptionId],
                id: [option.id],
                elements: this.fb.array([]),
              });

              option.elements.forEach((subOption: any) => {
                (optionGroup.get('elements') as FormArray).push(
                  this.fb.group({
                    elementOptionRelationshipId: [subOption.elementOptionRelationshipId],
                    elementType: [subOption.elementType],
                    label: [subOption.label],
                    order: [subOption.order, Validators.required],
                    required: [subOption.required],
                    labelName: [subOption.labelName],
                  }),
                );
              });

              (elementGroup.get('options') as FormArray).push(optionGroup);
            });

            (criteriaGroup.get('elements') as FormArray).push(elementGroup);
          });

          (taskGroup.get('acceptanceCriterias') as FormArray).push(criteriaGroup);
        });

        (stageGroup.get('tasks') as FormArray).push(taskGroup);
      });

      this.accordionStages.push(stageGroup);
    });
    this.cdr.detectChanges(); // Marcar manualmente la detección de cambios
  }

  getStageFormArray(): FormArray {
    return this.secondFormGroup.get('stages') as FormArray;
  }

  removeStage(index: number) {
    this.accordionStages.removeAt(index);
  }

  addStageAccordion() {
    const stageGroup = this.fb.group({
      stageName: ['', Validators.required],
      tasks: this.fb.array([]),
      order: [null, Validators.required],
      required: [false],
    });
    this.accordionStages.push(stageGroup);
  }

  onSubmit() {
    this.showProgressBar = true;
    if (this.firstFormGroup.valid) {
      const formData = {
        id: this.maintenanceId,
        ...this.firstFormGroup.value,
        stages: this.accordionStages.value,
      };
      this.maintenanceService.updateMaintenance(this.maintenanceId, formData).subscribe(
        () => {
          this.showProgressBar = false;
          this.router.navigate(['/maintenances']);
        },
        (error: HttpErrorResponse) => {
          // Verifica si el cuerpo de la respuesta contiene el mensaje de error
          if (error.status === 409 && error.error) {
            this.errorMessage = error.error;
          } else if (error.message) {
            this.errorMessage = error.message;
          } else {
            this.errorMessage = `Error desconocido (${error.status}): ${error.statusText}`;
          }
          this.showProgressBar = false;
        },
      );
    } else {
      this.markAllFieldsAsTouched();
      this.showProgressBar = false;
    }
  }

  markAllFieldsAsTouched() {
    Object.keys(this.firstFormGroup.controls).forEach(field => {
      const control = this.firstFormGroup.get(field);
      control?.markAsTouched({ onlySelf: true });
    });

    this.accordionStages.controls.forEach(stageGroup => {
      const stageFormGroup = stageGroup as FormGroup;
      Object.keys(stageFormGroup.controls).forEach(field => {
        const control = stageFormGroup.get(field);
        control?.markAsTouched({ onlySelf: true });
      });

      const tasksFormArray = stageFormGroup.get('tasks') as FormArray;
      tasksFormArray.controls.forEach((taskGroup: AbstractControl) => {
        const taskFormGroup = taskGroup as FormGroup;
        Object.keys(taskFormGroup.controls).forEach(field => {
          const control = taskFormGroup.get(field);
          control?.markAsTouched({ onlySelf: true });
        });

        const acceptanceCriterias = taskFormGroup.get('acceptanceCriterias') as FormArray;
        acceptanceCriterias.controls.forEach((criteriaGroup: AbstractControl) => {
          const criteriaFormGroup = criteriaGroup as FormGroup;
          Object.keys(criteriaFormGroup.controls).forEach(field => {
            const control = criteriaFormGroup.get(field);
            control?.markAsTouched({ onlySelf: true });
          });

          const criteriaElements = criteriaFormGroup.get('elements') as FormArray;
          criteriaElements.controls.forEach((elementGroup: AbstractControl) => {
            const elementFormGroup = elementGroup as FormGroup;
            Object.keys(elementFormGroup.controls).forEach(field => {
              const control = elementFormGroup.get(field);
              control?.markAsTouched({ onlySelf: true });
            });
          });
        });
      });
    });
  }

  getFormGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }
}
